import { useQueryClient } from "@tanstack/react-query";
import { ChevronRight, FileWarning, Plus } from "lucide-react";
import { useState } from "react";

import {
  useAssignDocumentToPatient,
  useReprocessDocument,
} from "@elation/api-client/src/stitch-web";
import type { UnmatchedPatientUpload } from "@elation/api-client/src/stitch-web/models";

import { DocumentEntity, DocumentViewer } from "@src/features/documents";
import { CreatePatientPopoverV2 } from "@src/features/patients/v2";
import { useIsPhysician } from "@src/features/physicians";
import { VerticalFlexbox } from "@src/shared/components/layout/Flexbox";
import { Button } from "@src/shared/components/ui/button";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@src/shared/components/ui/collapsible";
import { pluralize } from "@src/shared/utils/strings";

import { CareProtocolOpportunityEntity } from "../../../../../../domain";
import { UploadHistoryFileNameTrigger } from "../UploadHistoryFileNameTrigger";

export interface NeedsReviewPatientUploadCardProps {
  item: UnmatchedPatientUpload;
}

export function NeedsReviewPatientUploadCard({
  item,
}: NeedsReviewPatientUploadCardProps) {
  const canCreatePatient = useIsPhysician();
  const queryClient = useQueryClient();
  const assignDocument = useAssignDocumentToPatient();
  const reprocessDocument = useReprocessDocument();
  const [isOpen, setIsOpen] = useState(false);

  const refreshUploadHistory = async () => {
    await queryClient.invalidateQueries({
      predicate: (query) => {
        const key = query.queryKey[0];
        return (
          key === "/gw/stitch-web/api/documents/unmatched-patient-uploads" ||
          key === "/gw/stitch-web/api/documents/care-gap-uploads" ||
          key === "/gw/stitch-web/api/care-protocol-opportunities"
        );
      },
    });
  };

  const assignAndReprocessDocument = async (patientId: number) => {
    await assignDocument.mutateAsync({
      id: item.document.id,
      data: { patientId },
    });
    try {
      await reprocessDocument.mutateAsync({ id: item.document.id });
      await refreshUploadHistory();
    } catch (err) {
      await assignDocument.mutateAsync({
        id: item.document.id,
        data: { patientId: null },
      });
      await refreshUploadHistory();
      throw err;
    }
  };

  return (
    <li key={item.document.id}>
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <div className="grid grid-cols-[minmax(150px,1.15fr)_minmax(160px,1.1fr)_120px_80px_72px] items-center gap-3 px-3 py-2.5">
          <CollapsibleTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              className="h-auto min-w-0 justify-start gap-2 rounded-[8px] px-0 py-0 text-left font-normal shadow-none hover:bg-transparent hover:text-foreground"
            >
              <ChevronRight
                className={`size-4 shrink-0 text-muted-foreground transition-transform ${isOpen ? "rotate-90" : ""}`}
                aria-hidden="true"
              />
              <div className="flex size-[28px] shrink-0 items-center justify-center rounded-[8px] bg-destructive/10 text-destructive">
                <FileWarning className="size-[14px]" aria-hidden="true" />
              </div>
              <VerticalFlexbox className="min-w-0 flex-1">
                <span className="truncate text-[13px] font-semibold text-surface-foreground">
                  Needs review
                </span>
                <span className="mt-0.5 truncate font-mono text-[10px] text-muted-foreground">
                  {DocumentEntity.needsReviewIdentifierSummaryLabel(
                    item.identifierSummary,
                  )}
                </span>
              </VerticalFlexbox>
            </Button>
          </CollapsibleTrigger>

          <div className="min-w-0">
            <DocumentViewer
              documentId={item.document.id}
              fileName={item.document.fileName}
              uploadedAtLabel={CareProtocolOpportunityEntity.uploadTimeLabel(
                item.completedAt,
              )}
              triggerTooltip={item.document.fileName}
              trigger={
                <UploadHistoryFileNameTrigger
                  fileName={item.document.fileName}
                />
              }
            />
          </div>
          <span className="font-mono text-[10.5px] text-muted-foreground">
            {CareProtocolOpportunityEntity.uploadTimeLabel(item.completedAt)}
          </span>
          <span className="font-mono text-[10.5px] text-muted-foreground">
            0 items
          </span>
          <CreatePatientPopoverV2
            canCreatePatient={canCreatePatient}
            onCreated={async (patient) => {
              if (patient?.id == null) return;
              await assignAndReprocessDocument(patient.id);
            }}
            trigger={
              <Button
                type="button"
                aria-label="Create new patient"
                variant="ghost"
                className="h-7 justify-end gap-1 rounded-[7px] px-0 text-[12px] font-semibold text-primary hover:bg-transparent hover:text-primary"
              >
                <Plus className="size-3.5" aria-hidden="true" />
                Create
              </Button>
            }
          />
        </div>
        <CollapsibleContent>
          <VerticalFlexbox className="gap-1 bg-muted/20 px-4 py-3">
            <span className="text-[12.5px] text-surface-foreground">
              {DocumentEntity.needsReviewReasonLabel(item.reason)}
            </span>
            <span className="font-mono text-[10.5px] text-muted-foreground">
              {DocumentEntity.needsReviewIdentifierSummaryLabel(
                item.identifierSummary,
              )}
            </span>
            <span className="font-mono text-[10.5px] text-muted-foreground">
              {pluralize(item.candidateCount, "candidate")} found
            </span>
          </VerticalFlexbox>
        </CollapsibleContent>
      </Collapsible>
    </li>
  );
}
